import { useEffect, useRef, useState } from 'react';
import { getChoseong } from 'es-hangul';
import styled from 'styled-components';

interface Option {
  id: number;
  name: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
}

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = '선택하세요',
}: SearchableSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 선택된 옵션의 이름 가져오기
  const selectedOption = options.find((opt) => opt.id === value);

  // 검색 필터링 (한글 초성 검색 지원)
  const filteredOptions = options.filter((option) => {
    if (!searchText) return true;
    const lowerSearch = searchText.toLowerCase();
    const lowerName = option.name.toLowerCase();

    // 일반 텍스트 검색 (부분 일치)
    if (lowerName.includes(lowerSearch)) return true;

    // 한글 초성 검색
    const choseong = getChoseong(option.name);
    return choseong.includes(searchText);
  });

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 드롭다운 열릴 때 입력창 포커스
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (option: Option) => {
    onChange(option.id);
    setIsOpen(false);
    setSearchText('');
  };

  const handleClear = (e: React.MouseEvent) => {
    console.log('clear!!');
    e.stopPropagation();
    onChange(undefined);
    setSearchText('');
  };

  return (
    <Container ref={containerRef}>
      <SelectTrigger onClick={() => setIsOpen(!isOpen)} hasValue={!!selectedOption}>
        {selectedOption ? (
          <>
            <SelectedText>{selectedOption.name}</SelectedText>
            <ClearButton onClick={handleClear}>×</ClearButton>
          </>
        ) : (
          <Placeholder>{placeholder}</Placeholder>
        )}
        <Arrow isOpen={isOpen}>▼</Arrow>
      </SelectTrigger>

      {isOpen && (
        <Dropdown>
          <SearchInputWrapper>
            <SearchInput
              ref={inputRef}
              type="text"
              placeholder="검색 (초성 검색 가능)"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                // Enter 키 눌렀을 때 form submit 방지
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            />
          </SearchInputWrapper>
          <OptionList>
            {filteredOptions.length === 0 ? (
              <NoResult>검색 결과가 없습니다</NoResult>
            ) : (
              filteredOptions.map((option) => (
                <OptionItem
                  key={option.id}
                  isSelected={option.id === value}
                  onClick={() => handleSelect(option)}>
                  {option.name}
                </OptionItem>
              ))
            )}
          </OptionList>
        </Dropdown>
      )}
    </Container>
  );
};

export default SearchableSelect;

// Styled Components
const Container = styled.div`
  position: relative;
  min-width: 12rem;
`;

const SelectTrigger = styled.div<{ hasValue: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  background-color: white;
  cursor: pointer;
  font-size: 0.875rem;
  min-height: 2.25rem;

  &:hover {
    border-color: #04c9db;
  }
`;

const SelectedText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Placeholder = styled.span`
  color: #999;
  flex: 1;
`;

const ClearButton = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  padding: 0 0.25rem;
  font-size: 1rem;
  color: #999;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #333;
  }
`;

const Arrow = styled.span<{ isOpen: boolean }>`
  font-size: 0.625rem;
  margin-left: 0.5rem;
  color: #666;
  transition: transform 0.2s ease;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.25rem;
  background: white;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
`;

const SearchInputWrapper = styled.div`
  padding: 0.5rem;
  border-bottom: 1px solid #eee;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #04c9db;
  }
`;

const OptionList = styled.div`
  max-height: 15rem;
  overflow-y: auto;
`;

const OptionItem = styled.div<{ isSelected: boolean }>`
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.875rem;
  background-color: ${({ isSelected }) => (isSelected ? '#e6f9fb' : 'transparent')};
  color: ${({ isSelected }) => (isSelected ? '#04c9db' : '#333')};
  font-weight: ${({ isSelected }) => (isSelected ? '600' : '400')};

  &:hover {
    background-color: ${({ isSelected }) => (isSelected ? '#e6f9fb' : '#f5f5f5')};
  }
`;

const NoResult = styled.div`
  padding: 1rem;
  text-align: center;
  color: #999;
  font-size: 0.875rem;
`;
